import type { Partner } from "@expert/shared-types";

import { useQuery } from "@tanstack/react-query";

import type { SalesAnalyticsDispatcher } from "../../../../../sales-context/SalesContext.types";
import type { CustomerDetailsPayload } from "../../../../../types";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { DEFAULT_COUNTRY_CODE, validatePhoneNumber } from "../../../../../shared-utils";
import { getCustomerDetails } from "../customer-info/getCustomerDetails";

const CUSTOMER_DETAILS_QUERY_KEY = "customer-details";

interface UseGetCustomerDetailsParams {
    mdn?: string;
    sessionId?: string;
    partner: Partner;
    dispatcher?: SalesAnalyticsDispatcher;
    customerDetailsCallback?: (result: CustomerDetailsPayload) => void;
    verificationType: string;
    shouldSkipMdnValidation?: boolean;
}

export function useGetCustomerDetails({
    mdn,
    partner,
    dispatcher,
    sessionId,
    customerDetailsCallback,
    verificationType,
    shouldSkipMdnValidation = false,
}: UseGetCustomerDetailsParams) {
    const { logger, apiClients } = useSalesContext();
    const childLogger = logger.child({ module: "useGetCustomerDetails" });

    const { data, isLoading } = useQuery({
        queryKey: [CUSTOMER_DETAILS_QUERY_KEY, mdn],
        queryFn: async () => {
            return getCustomerDetails(apiClients.customer, childLogger, {
                mdn: mdn ?? "",
                partner,
                sessionId,
                searchType: "phoneNumber",
                dispatcher,
                customerDetailsCallback,
                modelResultsMdn: mdn,
            });
        },
        enabled:
            verificationType === "phoneNumber" &&
            validatePhoneNumber(mdn, DEFAULT_COUNTRY_CODE, shouldSkipMdnValidation).valid,
        // Avoid refetching just because the component remounted (e.g. entering "Edit" mode).
        // We only want to fetch when the query key changes (MDN changes) or via an explicit refetch.
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    return {
        data,
        isLoading,
    };
}
